/**
 * Metadata-driven list: search, filters (status / select / boolean / relation / date range),
 * sortable columns, pagination, CSV export, "new" button. State lives in the URL for page lists
 * and in component state when embedded (record tabs, hubs) with fixed filters.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, saveFile } from '../lib/api.ts';
import { useQuery, useDebounced } from '../lib/hooks.ts';
import { useI18n } from '../lib/i18n.tsx';
import { useRoute, Link } from '../lib/router.tsx';
import { useSession } from '../lib/session.tsx';
import { Button, EmptyState, ErrorState, Pager, useToast } from './ui.tsx';
import { Icon } from './Icon.tsx';
import { FieldValue, NUMERIC_TYPES } from './FieldValue.tsx';
import { RecordPicker } from './Form.tsx';
import type { EntityMeta, FieldMeta, ListResult, Row } from '../lib/types.ts';

export interface ListState { page: number; pageSize: number; sort?: string; dir?: 'asc' | 'desc'; search: string; filters: Record<string, string> }

function useListState(embedded: boolean, def: EntityMeta): [ListState, (patch: Partial<ListState>) => void] {
  const { query, setQuery } = useRoute();
  const [local, setLocal] = useState<ListState>({ page: 1, pageSize: 10, search: '', filters: {} });
  const fromUrl = useMemo<ListState>(() => {
    const filters: Record<string, string> = {};
    for (const [k, v] of query) { const m = /^f\.(.+)$/.exec(k); if (m) filters[m[1]] = v; }
    return { page: Number(query.get('page') ?? 1) || 1, pageSize: Number(query.get('pageSize') ?? 10) || 10, sort: query.get('sort') ?? undefined, dir: (query.get('dir') as 'asc' | 'desc' | null) ?? undefined, search: query.get('q') ?? '', filters };
  }, [query]);
  const state = embedded ? local : fromUrl;
  const set = useCallback((patch: Partial<ListState>) => {
    if (embedded) { setLocal((s) => ({ ...s, ...patch })); return; }
    const next = { ...fromUrl, ...patch };
    const q: Record<string, string | number | null> = { page: next.page === 1 ? null : next.page, pageSize: next.pageSize === 10 ? null : next.pageSize, sort: next.sort ?? null, dir: next.dir ?? null, q: next.search || null };
    for (const k of Object.keys(fromUrl.filters)) q[`f.${k}`] = null;
    for (const [k, v] of Object.entries(next.filters)) q[`f.${k}`] = v || null;
    setQuery(q);
  }, [embedded, fromUrl, setQuery]);
  void def;
  return [state, set];
}

function FilterInput({ field, value, onChange }: { field: FieldMeta; value: string; onChange: (v: string) => void }) {
  const { t } = useI18n();
  if (field.type === 'status' || field.type === 'select') {
    return <select className="input sm" value={value} onChange={(e) => onChange(e.target.value)}><option value="">{t('common.all')}</option>{field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
  }
  if (field.type === 'boolean') return <select className="input sm" value={value} onChange={(e) => onChange(e.target.value)}><option value="">{t('common.all')}</option><option value="true">{t('common.yes')}</option><option value="false">{t('common.no')}</option></select>;
  if (field.type === 'relation' && field.relation) return <div style={{ minWidth: 220 }}><RecordPicker entity={field.relation.entity} display={field.relation.display} value={value || null} onChange={(id) => onChange(id ?? '')} small /></div>;
  if (field.type === 'date' || field.type === 'datetime') {
    const [from, to] = value.split('..');
    return <span className="input-group"><input type="date" className="input sm" value={from ?? ''} onChange={(e) => onChange(`${e.target.value}..${to ?? ''}`)} /><span className="muted">–</span><input type="date" className="input sm" value={to ?? ''} onChange={(e) => onChange(`${from ?? ''}..${e.target.value}`)} /></span>;
  }
  return <input className="input sm" value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.label} />;
}

function toApiFilters(def: EntityMeta, filters: Record<string, string>, fixed: Record<string, unknown> = {}): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(fixed)) if (v !== undefined && v !== null) out[`filter[${k}]`] = String(v);
  for (const [k, v] of Object.entries(filters)) {
    if (!v) continue;
    const f = def.fields.find((x) => x.name === k);
    if (f && (f.type === 'date' || f.type === 'datetime')) {
      const [from, to] = v.split('..');
      if (from) out[`filter[${k}][gte]`] = from;
      if (to) out[`filter[${k}][lte]`] = to;
    } else if (f && (f.type === 'text' || f.type === 'textarea' || f.type === 'code')) out[`filter[${k}][like]`] = v;
    else out[`filter[${k}]`] = v;
  }
  return out;
}

export function EntityList({ def, embedded = false, fixed = {}, columns, title, createDefaults, onRowClick, actions, compact, pageSize: initialPageSize, hideToolbar, selectable }: {
  def: EntityMeta; embedded?: boolean; fixed?: Record<string, unknown>; columns?: string[]; title?: ReactNode; createDefaults?: Record<string, unknown>;
  onRowClick?: (row: Row) => void; actions?: ReactNode; compact?: boolean; pageSize?: number; hideToolbar?: boolean; selectable?: { onSelect: (row: Row) => void };
}) {
  const { t } = useI18n();
  const { navigate } = useRoute();
  const { can } = useSession();
  const toast = useToast();
  const [state, set] = useListState(embedded, def);
  const [showFilters, setShowFilters] = useState(Object.keys(state.filters).length > 0);
  const [searchText, setSearchText] = useState(state.search);
  const dSearch = useDebounced(searchText, 300);
  useEffect(() => { if (dSearch !== state.search) set({ search: dSearch, page: 1 }); }, [dSearch]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (initialPageSize && embedded) set({ pageSize: initialPageSize }); }, [initialPageSize, embedded]); // eslint-disable-line react-hooks/exhaustive-deps
  const cols = useMemo(() => (columns ?? def.listColumns).map((c) => def.fields.find((f) => f.name === c)).filter((f): f is FieldMeta => !!f && !f.sensitive), [columns, def]);
  const filterFields = useMemo(() => def.filters.map((c) => def.fields.find((f) => f.name === c)).filter((f): f is FieldMeta => !!f && !(f.name in fixed)), [def, fixed]);
  const params = useMemo(() => ({ page: state.page, pageSize: state.pageSize, sort: state.sort, dir: state.dir, search: state.search || undefined, ...toApiFilters(def, state.filters, fixed) }), [state, def, fixed]);
  const key = `list:${def.name}:${JSON.stringify(params)}`;
  const { data, error, loading, refresh } = useQuery(key, () => api.get<ListResult>(def.name, params), { keepPrevious: true, ttlMs: 0 });
  const sortBy = (f: FieldMeta) => { if (f.sortable === false) return; set({ sort: f.name, dir: state.sort === f.name && state.dir === 'asc' ? 'desc' : 'asc', page: 1 }); };
  const exportCsv = async () => {
    try {
      const r = await api.get<{ fileName: string; content: string; mimeType: string }>(`export/${def.name}`, { format: 'csv', search: state.search || undefined, ...toApiFilters(def, state.filters, fixed), columns: cols.map((c) => c.name).join(',') });
      saveFile(r.fileName, r.content, r.mimeType);
    } catch (e) { toast.error(e); }
  };
  const newPath = `${def.route}/new${createDefaults ? `?${new URLSearchParams(Object.entries(createDefaults).map(([k, v]) => [`d.${k}`, String(v)])).toString()}` : ''}`;
  const rowHref = (r: Row) => `${def.route}/${r.id}`;
  const canCreate = def.can?.create ?? can(`${def.module}.${def.name}.create`);
  return (
    <section className={`card ${compact ? 'compact' : ''}`}>
      {(title || !hideToolbar) && (
        <div className="toolbar">
          {title && <h3 style={{ fontSize: 15 }}>{title}</h3>}
          {!hideToolbar && (
            <>
              <div className="search"><Icon name="Search" /><input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder={t('common.searchPlaceholder')} style={{ border: 0, background: 'transparent', font: 'inherit', width: '100%', outline: 'none', color: 'var(--fg)' }} /></div>
              {filterFields.length > 0 && <Button size="sm" icon="Filter" onClick={() => setShowFilters((s) => !s)} className={showFilters ? 'primary' : ''}>{t('common.filters')}{Object.values(state.filters).filter(Boolean).length ? ` (${Object.values(state.filters).filter(Boolean).length})` : ''}</Button>}
              <Button size="sm" icon="RefreshCw" onClick={() => void refresh()} title={t('common.refresh')} />
              {(def.can?.export ?? can(`${def.module}.${def.name}.export`)) && <Button size="sm" icon="Download" onClick={() => void exportCsv()}>{t('common.export')}</Button>}
            </>
          )}
          <span className="spacer" />
          {actions}
          {canCreate && !selectable && !actions && <Link to={newPath} className="btn primary sm"><Icon name="Plus" size={16} /> {t('common.new')}</Link>}
        </div>
      )}
      {showFilters && filterFields.length > 0 && (
        <div className="filters-bar">
          {filterFields.map((f) => <div className="field" key={f.name}><label>{f.label}</label><FilterInput field={f} value={state.filters[f.name] ?? ''} onChange={(v) => set({ filters: { ...state.filters, [f.name]: v }, page: 1 })} /></div>)}
          <Button size="sm" variant="ghost" onClick={() => set({ filters: {}, page: 1 })}>{t('common.clearFilters')}</Button>
        </div>
      )}
      {error ? <div className="card-b"><ErrorState error={error} retry={() => void refresh()} /></div> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr>{cols.map((c) => <th key={c.name} className={`${NUMERIC_TYPES.has(c.type) ? 'num' : ''} ${c.sortable !== false ? 'sortable' : ''}`} onClick={() => sortBy(c)}>{c.label}{state.sort === c.name && <span className="sort">{state.dir === 'desc' ? '↓' : '↑'}</span>}</th>)}{selectable && <th />}</tr></thead>
            <tbody>
              {loading && !data ? Array.from({ length: 5 }).map((_, i) => <tr key={i}>{cols.map((c) => <td key={c.name}><div className="skeleton" /></td>)}{selectable && <td />}</tr>)
                : !data || data.rows.length === 0 ? <tr><td colSpan={cols.length + (selectable ? 1 : 0)}><EmptyState title={t('common.noData')} hint={canCreate && !selectable ? t('common.createFirst', { entity: def.label }) : undefined} action={canCreate && !selectable && !actions ? <Link to={newPath} className="btn primary sm"><Icon name="Plus" size={16} /> {t('common.new')}</Link> : undefined} /></td></tr>
                : data.rows.map((r) => (
                  <tr key={String(r.id)} className={onRowClick || !selectable ? 'clickable' : ''} onClick={() => (onRowClick ? onRowClick(r) : selectable ? undefined : navigate(rowHref(r)))}>
                    {cols.map((c, i) => <td key={c.name} className={NUMERIC_TYPES.has(c.type) ? 'num' : ''}>{i === 0 && !onRowClick && !selectable ? <Link to={rowHref(r)} className="link" onClick={(e) => e.stopPropagation()}><FieldValue field={c} row={r} relations={data.relations} link={false} /></Link> : <FieldValue field={c} row={r} relations={data.relations} />}</td>)}
                    {selectable && <td className="actions-cell"><Button size="sm" onClick={() => selectable.onSelect(r)}>{t('common.select')}</Button></td>}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.total > 0 && <Pager page={data.page} pageSize={data.pageSize} total={data.total} onPage={(p) => set({ page: p })} onPageSize={(n) => set({ pageSize: n, page: 1 })} />}
    </section>
  );
}
